import type { VideoLayout } from '../state.ts';

export interface VideoOverlay {
  setSource: (file: File) => void;
  clear: () => void;
  hasVideo: () => boolean;
  name: () => string | null;
  setLayout: (layout: VideoLayout) => void;
  setVideoOpacity: (o: number) => void;
  setSceneOpacity: (o: number) => void;
  setOnTop: (onTop: boolean) => void;
  /** The underlying <video> — the MP4 exporter seeks + draws it per frame. */
  element: () => HTMLVideoElement;
  /** Sync the video to the mocap playhead (called whenever the frame changes). */
  sync: (frame: number, fps: number, playing: boolean, frameDelta: number) => void;
}

/**
 * A reference-video overlay positioned over the WebGL canvas (not composited into
 * it — it's a visual reference only). The <video> element is seeked/played to
 * follow the mocap timeline, with a frame offset for imperfect start alignment.
 */
export function createVideoOverlay(
  canvas: HTMLCanvasElement,
  onReady: () => void,
  onError: (message: string) => void
): VideoOverlay {
  const layer = document.getElementById('video-layer') as HTMLElement;
  const video = document.getElementById('ref-video') as HTMLVideoElement;

  let objectUrl: string | null = null;
  let currentName: string | null = null;
  let layout: VideoLayout = 'pip';
  let onTop = true;

  // Seek coalescing: only one seek in flight at a time. While the browser is
  // decoding a seek, later targets are collapsed into `pendingSeek` and the most
  // recent one is applied on `seeked`. This stops the thrash during a fast scrub
  // (so it converges to where you let go instead of decoding every intermediate).
  let seeking = false;
  let pendingSeek: number | null = null;

  video.muted = true;
  video.playsInline = true;
  video.preload = 'auto';

  function requestSeek(target: number): void {
    if (Math.abs(video.currentTime - target) < 0.01) return;
    if (seeking) {
      pendingSeek = target;
      return;
    }
    seeking = true;
    video.currentTime = target;
  }

  video.addEventListener('seeked', () => {
    seeking = false;
    if (pendingSeek !== null) {
      const next = pendingSeek;
      pendingSeek = null;
      requestSeek(next);
    }
  });

  // Sync to the playhead once the video knows its duration.
  video.addEventListener('loadedmetadata', () => onReady());
  // Surface codecs the browser can't decode (e.g. HEVC without the OS extension).
  video.addEventListener('error', () => {
    seeking = false;
    pendingSeek = null;
    if (objectUrl) {
      onError("Couldn't decode this video — try an H.264 .mp4 (HEVC needs OS codec support).");
    }
  });

  function applyLayout(): void {
    layer.className = `video-layer ${layout}`;
    if (layout === 'overlay' && !onTop) {
      // Put the 3D canvas above the video (both can be semi-transparent).
      canvas.style.zIndex = '2';
      layer.style.zIndex = '1';
    } else {
      canvas.style.zIndex = '';
      layer.style.zIndex = '';
    }
    if (layout !== 'overlay') {
      canvas.style.opacity = '';
      layer.style.opacity = '';
    }
  }

  function setSource(file: File): void {
    clear();
    objectUrl = URL.createObjectURL(file);
    currentName = file.name;
    video.src = objectUrl;
    video.load();
    layer.hidden = false;
    applyLayout();
  }

  function clear(): void {
    if (objectUrl) {
      URL.revokeObjectURL(objectUrl);
      objectUrl = null;
    }
    seeking = false;
    pendingSeek = null;
    currentName = null;
    video.removeAttribute('src');
    video.load();
    layer.hidden = true;
    canvas.style.opacity = '';
    canvas.style.zIndex = '';
  }

  function sync(frame: number, fps: number, playing: boolean, frameDelta: number): void {
    if (!objectUrl) return;
    const duration = video.duration;
    if (!duration || Number.isNaN(duration)) return; // not ready yet

    const target = (frame + frameDelta) / fps;
    const outOfRange = target < 0 || target > duration;
    // Out of range → hide the frame so the layer's black backdrop shows.
    video.style.visibility = outOfRange ? 'hidden' : 'visible';
    if (outOfRange) {
      if (!video.paused) video.pause();
      return;
    }

    if (playing) {
      if (video.paused) void video.play().catch(() => {});
      // Let it play; only correct noticeable drift to avoid stutter.
      if (Math.abs(video.currentTime - target) > 0.2) requestSeek(target);
    } else {
      if (!video.paused) video.pause();
      requestSeek(target);
    }
  }

  return {
    setSource,
    clear,
    hasVideo: () => objectUrl !== null,
    name: () => currentName,
    setLayout: (l) => {
      layout = l;
      applyLayout();
    },
    setVideoOpacity: (o) => {
      layer.style.opacity = layout === 'overlay' ? String(o) : '';
    },
    setSceneOpacity: (o) => {
      canvas.style.opacity = layout === 'overlay' ? String(o) : '';
    },
    setOnTop: (t) => {
      onTop = t;
      applyLayout();
    },
    element: () => video,
    sync,
  };
}
